// MACHINERY V2 (Stroje) — the hero's NAME-BLOCK atoms (split out of
// ProfileHero.tsx at M2.16d step 2, near-cap rule).
//
// WHAT IT OWNS: the pieces that identify the machine by NAME rather than by
// field — the eyebrow, the brand line, the decommissioned pill, the catalog pill
// and the composed name row. Every one of them is now drawn by TWO heroes (the desktop
// section and the below-1280 band, ProfileHeroPhone.tsx), which is exactly why
// they moved: a second hero that re-inlined the h1 or the pill is how one
// machine ends up with two different names for the same state.
//
// WHY IT IS SEPARATE: ProfileHero.tsx stood at 288 of the project's 300-line cap
// and M2.16d adds a breakpoint branch to it. The split is the first task rather
// than the last, and the seam is "identity by name" (here) versus "identity by
// field" (ProfileIdentityRows.tsx) versus "composition + actions" (ProfileHero).
//
// FIELD-LEVEL identity is NOT here. Serial number, location, groups, taper and
// the spec KV grid all stay in ProfileIdentityRows.tsx, which binds data-package
// §2 parity as one block. The ONE exception is the S/N, which the phone band
// repeats under the name (board 20/21) — it reads `machine.serialNumber`
// directly there and does not fork the chip.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  getBrandDisplayName,
  getMachineDisplayName,
  getMachineName,
} from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { HealthDot } from "../fleet/fleetCardAtoms";

/**
 * The eyebrow above the machine name: the derived type line, plus "custom build"
 * when there is no catalog model behind it.
 *
 * MOVED HERE AT THE M2.16d FIX PASS (review item 3), MARKUP UNCHANGED. The
 * below-1280 band had simply dropped it, which left a CUSTOM machine with no
 * indication of that fact anywhere on a phone — the KATALÓG pill marks the
 * catalog case and nothing marked its complement. Restoring it by re-inlining
 * the two-line derivation in the second hero is how one machine ends up
 * described two ways, which is the whole reason this file exists; so the
 * derivation moved instead. The desktop hero renders the identical element from
 * the identical inputs.
 *
 * The `.eyebrow` class is the token sheet's 10px mono uppercase
 * (machinery-v2-tokens.scss:214-220) and needs no size fork between the 26px
 * desktop h1 and the 17px band h1.
 */
export const HeroEyebrow: React.FC<{ machine: Machine }> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  const text = [
    getMachineName(machine),
    machine.catalogModelId ? null : translate("myMachinery:v2.profile.customBuild"),
  ]
    .filter(Boolean)
    .join(" · ");
  if (!text) return null;
  return (
    <div className="eyebrow" style={{ color: "var(--mod-accent)", fontWeight: 700 }}>
      {text}
    </div>
  );
};

/** Brand line — flat, or `parent > child` when the catalog model carries a
 *  manufacturer hierarchy (§2; the hierarchy reads `catalogModel.manufacturer`,
 *  NOT the top-level one). */
export const BrandLine: React.FC<{ machine: Machine }> = ({ machine }) => {
  const brandName = getBrandDisplayName(machine);
  if (!brandName) return null;
  const parent = machine.catalogModel?.manufacturer?.parentManufacturerName;

  return (
    <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--muted)", minWidth: 0 }}>
      {parent ? (
        <>
          <span style={{ color: "var(--faint)" }}>{parent}</span>
          <span style={{ color: "var(--faint)", margin: "0 5px" }}>›</span>
          <span style={{ color: "var(--ink-2)" }}>
            {machine.catalogModel?.manufacturer?.name}
          </span>
        </>
      ) : (
        brandName
      )}
    </div>
  );
};

/** §2: on a retired machine this pill REPLACES the health dot — the two are
 *  mutually exclusive, which is why they are composed together in HeroNameRow
 *  and never rendered by a caller side by side. */
export const DecommissionedPill: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--faint)",
        background: "var(--card-2)",
        border: "1px dashed var(--border)",
        borderRadius: 999,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {translate("myMachinery:decommissioned")}
    </span>
  );
};

/**
 * Board 20/21's KATALÓG pill, top-right of the phone band.
 *
 * IT IS NOT A NEW CONCEPT AND NOT A NEW STRING. ProfileIdentityRows.tsx already
 * paints this exact pill inside the desktop chip row, gated on the same
 * `machine.catalogModelId` and translated from the same `myMachinery:catalogModel`
 * key. The phone band cannot show the whole chip row, so the ONE chip the board
 * keeps is lifted here — same gate, same key, same blue — rather than invented
 * beside it with a second label.
 */
export const CatalogPill: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        padding: "0 9px",
        borderRadius: 999,
        border: "1px solid rgba(52, 152, 219, 0.34)",
        background: "rgba(52, 152, 219, 0.10)",
        color: "#3498DB",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {translate("myMachinery:catalogModel")}
    </span>
  );
};

export interface HeroNameRowProps {
  machine: Machine;
  decommissioned: boolean;
}

/** The desktop hero's name line: h1 + (health dot XOR decommissioned pill) +
 *  brand line. Unchanged from the M2.4 markup — only its address changed. */
export const HeroNameRow: React.FC<HeroNameRowProps> = ({ machine, decommissioned }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 12,
      flexWrap: "wrap",
      minWidth: 0,
    }}
  >
    <h1
      style={{
        margin: 0,
        fontSize: 26,
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: "var(--ink)",
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {getMachineDisplayName(machine)}
    </h1>
    {/* Health dot and the decommissioned pill are mutually exclusive —
        §2: the pill REPLACES the dot. */}
    {decommissioned ? (
      <DecommissionedPill />
    ) : (
      <HealthDot openReports={machine.openServiceReportsCount} />
    )}
    <BrandLine machine={machine} />
  </div>
);
